import { Link, Navigate, useLocation } from 'react-router-dom';
import { AuthSplit } from '../../components/bidder-web';
import { SignInForm } from '../../components/SignInForm';
import { useSession } from '../../hooks/useSession';
import { authRedirectUrl } from '../../lib/authRedirect';

/**
 * Bidder sign-in for the shared app (app.auctisync.com).
 *
 * REBUILT to the web design handoff (`web/web-auth.jsx`, 2026-09-02) — the
 * brand split replaces a centred card. The FORM is untouched: `SignInForm` is
 * shared with the staff sign-in pages and owns the auth call and the
 * role-based routing, so a redesign here cannot change who lands where.
 *
 * "BROWSE WITHOUT AN ACCOUNT" IS OFFERED DIRECTLY, not hidden behind the form.
 * Watching and browsing need no account; saying so is worth more than the
 * conversions a wall would buy, and a bidder who signs up to look and then
 * finds they must ALSO be approved by the house has been mildly deceived.
 */
export function BidderSignIn() {
  const { session, loading } = useSession();
  const location = useLocation();
  if (!loading && session) {
    return <Navigate to="/" replace />;
  }

  // Honour a ProtectedRoute redirect: a bidder sent here from a lot goes back
  // to that lot, not to the house-routing home.
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  return (
    <AuthSplit>
      <div>
        <h2 className="w-h2">Sign in to bid</h2>
        <p className="w-body muted" style={{ marginTop: 8 }}>
          Not registered yet? <Link to="/signup">Create an account</Link>.
        </p>
      </div>

      <SignInForm
        magicLinkRedirectTo={authRedirectUrl(`/auth/callback?next=${encodeURIComponent(from)}`)}
        defaultRedirect={from}
        showMagicLink={false}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
        <Link to="/forgot-password" className="muted" style={{ fontSize: 13.5 }}>
          Forgot your password?
        </Link>
      </div>

      <div className="wau-sep">or</div>
      <Link to="/browse" className="w-btn w-btn-out w-btn-full">
        Browse without an account
      </Link>
      <p
        className="muted"
        style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55, textAlign: 'center' }}
      >
        Anyone can browse lots. Bidding and watching need an account with the house running the
        sale.
      </p>
    </AuthSplit>
  );
}
